import Link from "next/link";

import {
  fileResourceVisual,
  linkResourceVisual,
  ResourceCardContent,
  resourceCardClass,
  resourceCardInteractiveClass,
  resourceCardLineWidthClass,
  type ResourceVisual,
} from "@/components/content/resource-card";
import {
  fileTypeKind,
  fileTypeLabel,
} from "@/components/content/icons/file-type-icons";
import { cn } from "@/lib/utils";
import type { WorkspaceFile } from "@/lib/mlx-workspace-client";

export function ChannelFeedFileResource({ file }: { file: WorkspaceFile }) {
  return (
    <ChannelFeedResource
      href={`/api/client/files/${encodeURIComponent(file.id)}`}
      title={file.fileName}
      kind="file"
      file={file}
      meta={`${fileTypeLabel(fileTypeKind(file))} · ${formatBytes(file.byteSize)}`}
    />
  );
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

/*
 * A feed row's resource on the shared resource card. The row's sentence
 * already says what happened ("updated an artifact"), so the card carries
 * only the thing: its picture, its name and its facts.
 */
export function ChannelFeedResource({
  href,
  title,
  kind,
  meta,
  onClick,
  file,
}: {
  href?: string;
  title: string;
  kind: "document" | "spreadsheet" | "file" | "link";
  meta: string;
  onClick?: () => void;
  file?: { fileName: string; contentType: string };
}) {
  const visual: ResourceVisual =
    kind === "document"
      ? { mark: "document" }
      : kind === "spreadsheet"
        ? { mark: "sheet" }
        : kind === "link"
          ? linkResourceVisual(href)
          : fileResourceVisual(file ?? { fileName: title }, file && href);
  const content = <ResourceCardContent visual={visual} title={title} meta={meta} />;
  const frame = cn(resourceCardClass, resourceCardLineWidthClass, "mt-2");
  const className = cn(frame, resourceCardInteractiveClass);
  if (href?.startsWith("/workspace/") && (kind === "document" || kind === "spreadsheet"))
    return <Link href={href} className={className}>{content}</Link>;
  if (onClick)
    return (
      <button type="button" className={className} onClick={onClick}>
        {content}
      </button>
    );
  if (href)
    return (
      <a
        className={className}
        href={href}
        download={file?.fileName}
        target={file ? undefined : "_blank"}
        rel="noreferrer noopener"
      >
        {content}
      </a>
    );
  return <div className={frame}>{content}</div>;
}
